// AXIS ZERO - Share Card Generator
// Black bg + neon typography share cards, Web Share API + fallback

export class ShareCardGenerator {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.gameUrl = window.location.href;
  }

  getShareText(score, streakMultiplier = 0) {
    const timeText = score.toFixed(2);
    const streakText = streakMultiplier > 1 ? ` | x${streakMultiplier} streak` : '';
    return `${timeText}s on AXIS ZERO${streakText} - Beat this. ${this.gameUrl}`;
  }

  async generateShareCard(score, streakMultiplier = 0) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 600;
    this.canvas.height = 400;
    this.ctx = this.canvas.getContext('2d');

    const ctx = this.ctx;
    const mainColor = '#00E5FF';
    
    // Pure black background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 600, 400);

    // Subtle ground line
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = 2;
    ctx.shadowColor = mainColor;
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.moveTo(0, 300);
    ctx.lineTo(600, 300);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Score (large, centered)
    ctx.textAlign = 'center';
    ctx.font = 'bold 72px Orbitron, sans-serif';
    ctx.fillStyle = mainColor;
    ctx.shadowColor = mainColor;
    ctx.shadowBlur = 25;
    ctx.fillText(`${score.toFixed(2)}s`, 300, 165);

    // "Beat this." challenge
    ctx.font = 'bold 28px Rajdhani, sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = '#FFFFFF';
    ctx.shadowBlur = 5;
    ctx.fillText('Beat this.', 300, 215);

    // Streak (if any)
    if (streakMultiplier > 1) {
      ctx.font = '600 18px Rajdhani, sans-serif';
      ctx.fillStyle = '#FF1744';
      ctx.shadowColor = '#FF1744';
      ctx.shadowBlur = 8;
      ctx.fillText(`x${streakMultiplier} STREAK`, 300, 250);
    }

    // Title
    ctx.font = 'bold 24px Orbitron, sans-serif';
    ctx.fillStyle = mainColor;
    ctx.shadowColor = mainColor;
    ctx.shadowBlur = 10;
    ctx.fillText('AXIS ZERO', 300, 350);

    // Corner accents
    ctx.shadowBlur = 0;
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(15, 35); ctx.lineTo(15, 15); ctx.lineTo(35, 15);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(585, 35); ctx.lineTo(585, 15); ctx.lineTo(565, 15);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(15, 365); ctx.lineTo(15, 385); ctx.lineTo(35, 385);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(585, 365); ctx.lineTo(585, 385); ctx.lineTo(565, 385);
    ctx.stroke();

    return this.canvas.toDataURL('image/png');
  }

  downloadImage(dataUrl, filename = 'axis-zero-score.png') {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async shareNative(score, streakMultiplier = 0) {
    const text = this.getShareText(score, streakMultiplier);

    if (navigator.share) {
      try {
        const dataUrl = await this.generateShareCard(score, streakMultiplier);
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'axis-zero.png', { type: 'image/png' });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'AXIS ZERO',
            text: text,
            url: this.gameUrl,
            files: [file],
          });
          return { success: true, method: 'native-with-image' };
        } else {
          await navigator.share({
            title: 'AXIS ZERO',
            text: text,
            url: this.gameUrl,
          });
          return { success: true, method: 'native-text-only' };
        }
      } catch (e) {
        if (e.name === 'AbortError') {
          return { success: false, method: 'cancelled' };
        }
        try {
          await navigator.share({
            title: 'AXIS ZERO',
            text: text,
            url: this.gameUrl,
          });
          return { success: true, method: 'native-text-only' };
        } catch {
          return { success: false, method: 'failed' };
        }
      }
    }
    
    return { success: false, method: 'not-supported' };
  }

  getSocialShareUrls(score, streakMultiplier = 0) {
    const text = encodeURIComponent(this.getShareText(score, streakMultiplier));
    const url = encodeURIComponent(this.gameUrl);
    
    return {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
    };
  }

  copyCaption(score, streakMultiplier = 0) {
    const text = this.getShareText(score, streakMultiplier);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    return text;
  }

  copyLink() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(this.gameUrl);
    }
    return this.gameUrl;
  }
}

export const shareCardGenerator = new ShareCardGenerator();
export default shareCardGenerator;
